import React, { useEffect, useState } from 'react';
import api from '../services/api';
import SimplePieChart from '../components/SimplePieChart';
import CalendarWidget from '../components/CalendarWidget';

interface KpiTarget {
  kpi_code: string;
  target_value: number;
  description: string;
}

interface DashboardData {
  kpi1: number;
  kpi2: number;
  kpi3: number;
  kpi4: number;
  kpi5: number;
  kpi6: number;
  kpi7: number;
  kpi8: { men_avg: number; women_avg: number };
  kpi9: { total_proposals: number; implemented_percentage: number };
  kpi10: number;
  projectStats: { label: string; value: number; color: string }[];
}

interface CalendarEvent {
  id: number;
  title: string;
  date: Date;
  type: 'meeting' | 'event' | 'biblestudy' | 'project';
}

const DashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [targets, setTargets] = useState<KpiTarget[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'kpis' | 'calendar'>('kpis');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [targetsRes, kpi1, kpi2, kpi3, kpi4, kpi5, kpi6, kpi7, kpi8, kpi9, kpi10, meetings, events, bibleStudies, projects] = await Promise.all([
          api.get('kpi-targets/'),
          api.get('meetings/kpi_participation/'),
          api.get('meetings/kpi_frequency_by_class/'),
          api.get('events/kpi_participation/'),
          api.get('events/kpi_volunteers/'),
          api.get('surveys/kpi_communication/'),
          api.get('incidents/kpi_reduction/'),
          api.get('surveys/kpi_climate/'),
          api.get('bible-studies/kpi_participation/'),
          api.get('projects/kpi_generation/'),
          api.get('contents/kpi_production/'),
          api.get('meetings/'),
          api.get('events/'),
          api.get('bible-studies/'),
          api.get('projects/'),
        ]);

        setTargets(targetsRes.data);

        const projectList = projects.data;
        const projectStats = [
          { label: 'Proposto', value: projectList.filter((p: any) => p.status === 'Proposed').length, color: '#94a3b8' },
          { label: 'Em Análise', value: projectList.filter((p: any) => p.status === 'InReview').length, color: '#f97316' },
          { label: 'Aprovado', value: projectList.filter((p: any) => p.status === 'Approved').length, color: '#84cc16' },
          { label: 'Implementado', value: projectList.filter((p: any) => p.status === 'Implemented').length, color: '#16a34a' },
          { label: 'Rejeitado', value: projectList.filter((p: any) => p.status === 'Rejected').length, color: '#ef4444' },
        ].filter(item => item.value > 0);

        setData({
          kpi1: kpi1.data.kpi,
          kpi2: kpi2.data.kpi,
          kpi3: kpi3.data.kpi,
          kpi4: kpi4.data.kpi,
          kpi5: kpi5.data.kpi,
          kpi6: kpi6.data.reduction_percentage,
          kpi7: kpi7.data.kpi,
          kpi8: kpi8.data,
          kpi9: kpi9.data,
          kpi10: kpi10.data.total_contents,
          projectStats
        });

        const meetingEvents = meetings.data.map((m: any) => ({ id: m.id, title: m.title, date: new Date(m.start_time), type: 'meeting' }));
        const eventEvents = events.data.map((e: any) => ({ id: e.id, title: e.title, date: new Date(e.date), type: 'event' }));
        const bibleStudyEvents = bibleStudies.data.map((b: any) => ({ id: b.id, title: b.topic, date: new Date(b.date), type: 'biblestudy' }));
        const projectEvents = projectList.filter((p: any) => p.expected_completion_date).map((p: any) => ({
            id: p.id,
            title: `Entrega: ${p.title}`,
            date: new Date(p.expected_completion_date + 'T00:00:00'),
            type: 'project'
          }));

        setCalendarEvents([...meetingEvents, ...eventEvents, ...bibleStudyEvents, ...projectEvents]);

      } catch (error) {
        console.error("Error fetching dashboard data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Metas oficiais da documentação (Fallback)
  const OFFICIAL_TARGETS: Record<string, number> = {
    KPI1: 85,
    KPI2: 80,
    KPI3: 70,
    KPI4: 100,
    KPI5: 4.5,
    KPI6: 20,
    KPI7: 4.5,
    KPI8_MEN: 20,
    KPI8_WOMEN: 20,
    KPI9_PROPOSALS: 6,
    KPI9_IMPLEMENTED: 70,
    KPI10: 4
  };

  const getTargetValue = (code: string) => {
    const target = targets.find(t => t.kpi_code === code);
    return target ? target.target_value : OFFICIAL_TARGETS[code];
  };

  const getStatusColor = (value: number, target: number) => {
    return value >= target ? 'var(--success)' : 'var(--warning)';
  };

  if (loading) return <div style={{ padding: '20px' }}>Carregando Dashboard...</div>;
  if (!data) return <div>Erro ao carregar dados.</div>;

  const GroupTitle: React.FC<{ title: string; number: number }> = ({ title, number }) => (
    <h2 style={{ 
      gridColumn: '1 / -1', 
      fontSize: '1rem', 
      marginTop: '30px', 
      marginBottom: '15px', 
      paddingBottom: '8px', 
      borderBottom: '2px solid var(--accent-red)',
      color: 'var(--text-secondary)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    }}>
      <span style={{ backgroundColor: 'var(--accent-red)', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '0.8rem' }}>{number}</span>
      {title.toUpperCase()}
    </h2>
  );

  return (
    <div style={{ paddingBottom: '50px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Dashboard de Impacto</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Monitoramento de KPIs Oficiais - Projeto Conecta Terra</p>
        </div>
        <div style={{ display: 'flex', gap: '5px', background: 'var(--bg-card)', padding: '5px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          <button
            className={`btn ${activeTab === 'kpis' ? '' : 'btn-secondary'}`}
            onClick={() => setActiveTab('kpis')}
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}
          >
            KPIs Oficiais
          </button>
          <button
            className={`btn ${activeTab === 'calendar' ? '' : 'btn-secondary'}`}
            onClick={() => setActiveTab('calendar')}
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}
          >
            Calendário
          </button>
        </div>
      </div>

      {activeTab === 'kpis' && (
        <div className="dashboard-grid">
          
          {/* 1. PARTICIPAÇÃO E ENGAJAMENTO */}
          <GroupTitle title="Participação e Engajamento Familiar" number={1} />
          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 1 - Taxa de Participação (Encontros)</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi1, getTargetValue('KPI1')) }}>
                {data.kpi1}%
              </div>
            </div>
            <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI1')}%</div>
          </div>

          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 2 - Frequência Média por Turma</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi2, getTargetValue('KPI2')) }}>
                {data.kpi2}%
              </div>
            </div>
            <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI2')}%</div>
          </div>

          {/* 2. ENVOLVIMENTO NOS EVENTOS */}
          <GroupTitle title="Envolvimento nos Eventos do Colégio" number={2} />
          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 3 - Participação em Eventos Oficiais</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi3, getTargetValue('KPI3')) }}>
                {data.kpi3}%
              </div>
            </div>
            <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI3')}%</div>
          </div>

          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 4 - Apoio Voluntário (Cobertura)</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi4, getTargetValue('KPI4')) }}>
                {data.kpi4}%
              </div>
            </div>
            <div className="kpi-meta">Meta: 100% (Mín. 1 pai por turma)</div>
          </div>

          {/* 3. COMUNICAÇÃO E TRANSPARÊNCIA */}
          <GroupTitle title="Comunicação e Transparência" number={3} />
          <div className="kpi-card" style={{ gridColumn: 'span 2' }}>
            <div>
              <div className="kpi-label">KPI 5 - Índice de Comunicação Escola-Família</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi5, getTargetValue('KPI5')) }}>
                {data.kpi5.toFixed(1)} / 5.0
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '5px' }}>"Você se sente bem informado e ouvido pelo Colégio Terra do Saber?"</p>
            </div>
            <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI5')}</div>
          </div>

          {/* 4. IMPACTO EDUCACIONAL */}
          <GroupTitle title="Impacto Educacional e Clima Escolar" number={4} />
          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 6 - Redução de Ocorrências (Semestral)</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi6, getTargetValue('KPI6')) }}>
                {data.kpi6}%
              </div>
            </div>
            <div className="kpi-meta">Meta: Redução ≥ {getTargetValue('KPI6')}%</div>
          </div>

          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 7 - Percepção do Clima Escolar</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi7, getTargetValue('KPI7')) }}>
                {data.kpi7.toFixed(1)} / 5.0
              </div>
            </div>
            <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI7')}</div>
          </div>

          {/* 5. FORMAÇÃO EM VALORES */}
          <GroupTitle title="Formação em Valores" number={5} />
          <div className="kpi-card" style={{ gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="kpi-label">KPI 8 - Participação nos Estudos Bíblicos</div>
                <div style={{ display: 'flex', gap: '30px', marginTop: '10px' }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Homens (Segunda, 6h)</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: getStatusColor(data.kpi8.men_avg, getTargetValue('KPI8_MEN')) }}>{data.kpi8.men_avg}</div>
                    <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI8_MEN')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Mulheres (Final da Tarde)</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: getStatusColor(data.kpi8.women_avg, getTargetValue('KPI8_WOMEN')) }}>{data.kpi8.women_avg}</div>
                    <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI8_WOMEN')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 6. PRODUÇÃO E LEGADO */}
          <GroupTitle title="Produção e Legado" number={6} />
          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 9 - Geração de Propostas (Anual)</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi9.total_proposals, getTargetValue('KPI9_PROPOSALS')) }}>
                {data.kpi9.total_proposals}
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '5px' }}>
                Taxa de Implementação: <span style={{ color: getStatusColor(data.kpi9.implemented_percentage, getTargetValue('KPI9_IMPLEMENTED')) }}>{data.kpi9.implemented_percentage}%</span> (Meta: {getTargetValue('KPI9_IMPLEMENTED')}%)
              </p>
            </div>
            <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI9_PROPOSALS')} propostas</div>
          </div>

          <div className="kpi-card">
            <div>
              <div className="kpi-label">KPI 10 - Produção de Conteúdos</div>
              <div className="kpi-value" style={{ color: getStatusColor(data.kpi10, getTargetValue('KPI10')) }}>
                {data.kpi10}
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '5px' }}>Cartilhas, textos e vídeos gerados</p>
            </div>
            <div className="kpi-meta">Meta: ≥ {getTargetValue('KPI10')} materiais/ano</div>
          </div>

          <div className="card" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
            <h3 style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '20px', textTransform: 'uppercase' }}>
              Distribuição de Status de Projetos
            </h3>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <SimplePieChart data={data.projectStats} />
            </div>
          </div>

        </div>
      )}

      {activeTab === 'calendar' && (
        <CalendarWidget events={calendarEvents} />
      )}
    </div>
  );
};

export default DashboardPage;
